import './styles.css';
import { PunchBoard } from './board';
import { CODES_PARAM, loadConfig } from './config';
import { BoardHeader } from './header';
import { AttendanceHub } from './hub';

const root = document.getElementById('app')!;
const config = loadConfig();

if (config.codes.length === 0) {
  renderMisconfigured();
} else {
  void start();
}

async function start(): Promise<void> {
  const header = new BoardHeader(config);
  const board = new PunchBoard(config);

  root.replaceChildren(header.element, board.element);

  // Started after mounting, and deliberately not awaited: the header resolves over the network and
  // retries indefinitely, while punches must start arriving immediately. A board that waited for its
  // own logo before connecting would miss the first person through the door.
  header.start();

  const hub = new AttendanceHub(
    config,
    (punch) => board.show(punch),
    (state) => header.setConnectionState(state),
  );

  await hub.start();
}

/**
 * Shown when the URL carries no terminal codes.
 *
 * Without them the board would connect successfully and then sit silently forever, which on a wall
 * display is indistinguishable from a quiet day — so the failure is made explicit and actionable
 * instead. No code is echoed back, so this reveals nothing to a passer-by.
 *
 * This is the one failure the board *does* block on, and it is not a network failure: a missing code
 * cannot be retried into working, and no punch will ever arrive. A failed header lookup is the
 * opposite — punches keep coming, so the board keeps showing them.
 */
function renderMisconfigured(): void {
  const panel = document.createElement('section');
  panel.className = 'notice';

  const icon = document.createElement('span');
  icon.className = 'notice-icon';
  icon.innerHTML = `
    <svg viewBox="0 0 24 24" width="21" height="21" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
      <path d="M12 8v5M12 17h.01"/><circle cx="12" cy="12" r="9"/>
    </svg>`;

  const title = document.createElement('h1');
  title.className = 'notice-title';
  title.textContent = 'Display not configured';

  const body = document.createElement('p');
  body.className = 'notice-body';
  body.textContent =
    'This display needs at least one terminal code in its address to know which terminal to show.';

  const example = document.createElement('code');
  example.className = 'notice-example';
  example.textContent = `${window.location.origin}/?${CODES_PARAM}=<terminal-code>`;

  const hint = document.createElement('p');
  hint.className = 'notice-hint';
  hint.textContent = 'Separate multiple codes with commas to show several terminals on one display.';

  panel.append(icon, title, body, example, hint);
  root.replaceChildren(panel);
}
